import { toast } from "react-toastify";
import { useRef, useState, type ChangeEvent } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  Input,
  Button,
  Select,
  Modal,
  AccountContainer,
  ExternalContainer,
} from "../../components/Account/reusables";
import { EditIcon, EditProfPictureIcon } from "../../assets/accountIcons";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import {
  updatePersonalInfo,
  updateProfileImage,
} from "../../features/store/authSlice";
import { formatDateAndTime } from "../../utils/utils";
import { DeleteType } from "./types";

const Personal = () => {
  return (
    <ExternalContainer>
      <div className="col-6">
        <InfoHeader />
        <div className="col-6">
          <PersonalInfo />
          <Localization />
          <DeleteAccount />
        </div>
      </div>
    </ExternalContainer>
  );
};

const InfoHeader = () => {
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handlePickImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleImageChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file.");
      event.target.value = "";
      return;
    }

    if (file.size > 1024 * 1024) {
      toast.error("Profile image must be 1MB or less.");
      event.target.value = "";
      return;
    }

    try {
      setIsUploading(true);
      await dispatch(updateProfileImage(file)).unwrap();
      toast.success("Profile image updated successfully!");
    } catch (error) {
      toast.error("Failed to update profile image. Please try again.");
    } finally {
      setIsUploading(false);
      event.target.value = "";
    }
  };

  return (
    <section className="relative mb-20">
      <img
        className="h-[204px] rounded-lg w-full object-cover"
        src="/images/PersonalInfoBG.jpeg"
        alt="profile"
      />
      <div className="absolute top-4 right-4">
        <EditIcon />
      </div>
      <div className="absolute top-[132px]">
        <div className="row-4 items-center">
          <div className="relative flex-shrink-0 pl-6">
            <img
              src={user?.image_url || "/png/default.png"}
              alt="Profile"
              className="size-28 sm:size-40 rounded-full p-2 bg-profPixBG aspect-square object-cover"
            />
            <div className="absolute bottom-2 right-4">
              <button
                type="button"
                aria-label="Change profile picture"
                onClick={handlePickImage}
                disabled={isUploading}
              >
                <EditProfPictureIcon />
              </button>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageChange}
          />
          <span className="mt-12  lt-sm-medium-pry-900 pt-6 dark:text-white">
            Joining date:
            {user?.created_at ? formatDateAndTime(user.created_at) : ""}
          </span>
        </div>
      </div>
    </section>
  );
};

// ✅ Personal Information Form
const PersonalInfo = () => {
  const { user, loading } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();

  // ✅ Form Validation Schema
  const schema = yup.object({
    username: yup.string().required("Username is required"),
    firstname: yup.string().required("First name is required"),
    lastname: yup.string().required("Last name is required"),
    othername: yup.string().optional(),
    phone_number: yup.string().required("Phone number is required"),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      username: user?.username || "",
      firstname: user?.firstname || "",
      lastname: user?.lastname || "",
      othername: user?.othername || "",
      phone_number: user?.phone_number || "",
    },
  });

  const onSubmit = async (data: {
    username: string;
    firstname: string;
    lastname: string;
    othername?: string;
    phone_number: string;
  }) => {
    try {
      await dispatch(updatePersonalInfo(data)).unwrap();
      toast.success("Personal information updated successfully!");
    } catch (error) {
      toast.error("Failed to update personal information. Please try again.");
    }
  };

  return (
    <AccountContainer headerText="Personal Information">
      <form onSubmit={handleSubmit(onSubmit)} className="col-4 size-full">
        <Input
          type="text"
          placeholder="Username"
          register={register}
          name="username"
        />
        {errors.username && (
          <p className="text-red-500 text-xs">
            {String(errors.username.message)}
          </p>
        )}

        <Input
          type="text"
          placeholder="First Name"
          register={register}
          name="firstname"
        />
        {errors.firstname && (
          <p className="text-red-500 text-xs">
            {String(errors.firstname.message)}
          </p>
        )}

        <Input
          type="text"
          placeholder="Last Name"
          register={register}
          name="lastname"
        />
        {errors.lastname && (
          <p className="text-red-500 text-xs">
            {String(errors.lastname.message)}
          </p>
        )}

        <Input
          type="text"
          placeholder="Other Name"
          register={register}
          name="othername"
        />
        {errors.othername && (
          <p className="text-red-500 text-xs">
            {String(errors.othername.message)}
          </p>
        )}

        <Input
          type="text"
          placeholder="Phone Number"
          register={register}
          name="phone_number"
        />
        {errors.phone_number && (
          <p className="text-red-500 text-xs">
            {String(errors.phone_number.message)}
          </p>
        )}

        <div className="w-full flex justify-end">
          <Button
            text={loading ? "Saving..." : "Save changes"}
            disabled={loading}
          />
        </div>
      </form>
    </AccountContainer>
  );
};

// ✅ Localization Form
const Localization = () => {
  const { user, loading } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();

  const [country] = useState(user?.country || "");
  const [language] = useState(user?.preferredLanguage || "");

  const handleSave = async () => {
    try {
      await dispatch(
        updatePersonalInfo({ country, language: language }),
      ).unwrap();
      toast.success("Localization updated successfully!");
    } catch (error) {
      toast.error("Failed to update localization settings.");
    }
  };

  return (
    <AccountContainer headerText="Localisation and Language">
      <form className="col-4">
        <Select
          data={countryOptions}
          label="Country"
          value={country}
          // onChange={(e) => setCountry(e.target.value)}
        />
        <Select
          data={localizationOptions}
          label="Preferred Language"
          value={language}
          // onChange={(e) => setLanguage(e.target.value)}
        />
      </form>
      <div className="w-full flex justify-end">
        <Button
          text={loading ? "Saving..." : "Save changes"}
          onClick={handleSave}
          disabled={loading}
        />
      </div>
    </AccountContainer>
  );
};

const localizationOptions = [
  { value: "en", label: "English" },
  { value: "fr", label: "French" },
  { value: "es", label: "Spanish" },
  { value: "de", label: "German" },
  { value: "it", label: "Italian" },
  { value: "pt", label: "Portuguese" },
  { value: "ru", label: "Russian" },
  { value: "zh", label: "Chinese" },
  { value: "ja", label: "Japanese" },
  { value: "ko", label: "Korean" },
  { value: "ar", label: "Arabic" },
];

const countryOptions = [
  { value: "ng", label: "Nigeria" },
  { value: "en", label: "USA" },
  { value: "fr", label: "France" },
  { value: "es", label: "Spain" },
  { value: "de", label: "Germany" },
  { value: "it", label: "Italy" },
  { value: "pt", label: "Portugal" },
  { value: "ru", label: "Russia" },
  { value: "zh", label: "China" },
  { value: "ja", label: "Japan" },
  { value: "ko", label: "Korea" },
  { value: "ar", label: "Morrocco" },
];

const DeleteAccount = () => {
  const [openModal, setOpenModal] = useState(false);
  const [password, setPassword] = useState("");
  const DeleteContainer = ({ title, subtitle, children }: DeleteType) => {
    return (
      <div className="col-6 rounded-lg p-6 border border-border-border1 dark:border-tabs dark:bg-BG-dark">
        <div className="col-2">
          <span className="satoshi-xl-bold-delete">{title}</span>
          <p className="satoshi-sm-normal-toggleBG-dark dark:text-main-dark">
            {subtitle}
          </p>
        </div>
        {children}
      </div>
    );
  };
  return (
    <div>
      <DeleteContainer
        title="Delete Account"
        subtitle=" Deleting your account will permanently remove all your data. This
          action cannot be undone."
      >
        <Input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <div className="flex justify-between">
          <button className="py-3 satoshi-base-bold-main-light dark:text-main-dark">
            Forgot password?
          </button>
          <button
            onClick={() => setOpenModal(true)}
            className="w-fit py-3 px-5 rounded-lg satoshi-base-bold-delete bg-deleteBG"
          >
            Delete Account
          </button>
        </div>
      </DeleteContainer>

      <Modal isOpen={openModal} onClose={() => setOpenModal(false)}>
        <DeleteContainer
          title="Delete Account"
          subtitle="To delete your account enter the phrase “My name is DarkLordNathan” and also enter your password."
        >
          <div className="col-4">
            <Input type="text" placeholder="Enter Phrase" value="" />
            <Input type="password" placeholder="Password" value="" />
          </div>
          <div className="col-2 bp:row-2 justify-between">
            <button className="py-3 satoshi-base-bold-main-light dark:text-main-dark">
              Forgot password?
            </button>
            <div className="col-2 bp:row-2 items-center w-full">
              <button className="w-full py-3 px-5 rounded-lg satoshi-base-bold-delete bg-deleteBG">
                Delete Account
              </button>
              <Button text="Cancel" width="w-full" />
            </div>
          </div>
        </DeleteContainer>
      </Modal>
    </div>
  );
};

export default Personal;
